import {
	Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	ChangeDetectorRef
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { NetFacadeService } from '../../services/netfacade.service';
import { NavigationService, NavLocation, NavType } from '../../services/navigation.service';
import { NetViewTreeDto, NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { MessageService } from 'base';
import { GetSharesResultDto } from '../../../UvExplorerServer.Dtos.TS';
import { LicensingService, UserPermissionsService } from 'identity';
import { DateService } from 'base';


@Component({
	selector: 'agent-list',
	templateUrl: './agentlist.component.html',
	styleUrls: ['./agentlist.component.scss']
})

export class AgentListComponent implements OnInit, OnDestroy {

	@Input()
	public network: NetViewDto;
	public get hasNetwork(): boolean {
		return !!this.network;
	}

	private destroyed: boolean;
	private timerId: any;

	public cols: any[];
	public agents: AgentDto[];

	public displayNewAgentDialog: boolean;
	public displayDeleteAgentConfirm: boolean = false;

	private netViewTreeLoadedSub: Subscription;
	private netViewTreeChangedSub: Subscription;
	private netViewTreeUnloadedSub: Subscription;
	private sharesLoadedSub: Subscription;
	private sharesChangedSub: Subscription;
	private sharesUnloadedSub: Subscription;

	public installAgentUrl: string;

	public doCustomSort(event): void {
	}

	constructor(
		private netFacadeService: NetFacadeService,
		private navService: NavigationService,
		private messageService: MessageService,
		private licensingService: LicensingService,
		private dateService: DateService,
		private userPermissionsService: UserPermissionsService,
		private changeDetector: ChangeDetectorRef) {

		this.network = null;
		this.destroyed = false;
		this.timerId = undefined;
		this.agents = [];
		this.displayNewAgentDialog = false;
		this.installAgentUrl = "";

		this.cols = [
			{ field: 'Name', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'Description', header: 'Description', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'Created', header: 'Created', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'Modified', header: 'Modified', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'Guid', header: 'Agent ID', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ClientAgentGuid', header: 'Client ID', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ClientAgentMachine', header: 'Client Machine', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ClientAgentLastConfigPollTime', header: 'Last Client Poll', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ClientAgentSoftwareVersion', header: 'Software Version', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];

		this.netViewTreeLoadedSub = this.netFacadeService.onNetViewTreeLoaded.subscribe(() => {
			this.rebuildAgentList();
		});
		this.netViewTreeChangedSub = this.netFacadeService.onNetViewTreeChanged.subscribe(() => {
			this.rebuildAgentList();
		});
		this.netViewTreeUnloadedSub = this.netFacadeService.onNetViewTreeUnloaded.subscribe(() => {
			this.rebuildAgentList();
		});
		this.sharesLoadedSub = this.netFacadeService.onSharesLoaded.subscribe(() => {
			this.rebuildAgentList();
		});
		this.sharesChangedSub = this.netFacadeService.onSharesChanged.subscribe(() => {
			this.rebuildAgentList();
		});
		this.sharesUnloadedSub = this.netFacadeService.onSharesUnloaded.subscribe(() => {
			this.rebuildAgentList();
		});
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public ngOnInit() {
		return;
	}

	public ngOnDestroy(): void {

		this.destroyed = true;
		this.stopTimer();

		if (this.netViewTreeLoadedSub) {
			this.netViewTreeLoadedSub.unsubscribe();
			this.netViewTreeLoadedSub = undefined;
		}
		if (this.netViewTreeChangedSub) {
			this.netViewTreeChangedSub.unsubscribe();
			this.netViewTreeChangedSub = undefined;
		}
		if (this.netViewTreeUnloadedSub) {
			this.netViewTreeUnloadedSub.unsubscribe();
			this.netViewTreeUnloadedSub = undefined;
		}
		if (this.sharesLoadedSub) {
			this.sharesLoadedSub.unsubscribe();
			this.sharesLoadedSub = undefined;
		}
		if (this.sharesChangedSub) {
			this.sharesChangedSub.unsubscribe();
			this.sharesChangedSub = undefined;
		}
		if (this.sharesUnloadedSub) {
			this.sharesUnloadedSub.unsubscribe();
			this.sharesUnloadedSub = undefined;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.destroyed)
			return;

		if (changes["network"]) {
			this.rebuildAgentList();

			if (this.network) {
				this.installAgentUrl = `/api/session/agentsetup/${this.network.Guid}`;
			}
			else {
				this.installAgentUrl = '';
			}
		}
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	public get canManageAgents(): boolean {
		return this.userPermissionsService.canManageAgents;
	}

	private startTimer(): void {
		if (!this.timerId && this.agents && this.agents.length > 0) {

			// Calculate min config poll interval for all agents
			let minPollIntervalSecs = Number.MAX_SAFE_INTEGER;
			this.agents.forEach((agent: AgentDto) => {
				minPollIntervalSecs = Math.min(agent.PollInterval, minPollIntervalSecs);
			}, this);

			// Divide the min config poll interval in half to make the UI more responsive
			minPollIntervalSecs /= 2;

			// Ensure the min config poll interval isn't too small
			let MIN_ALLOWED_POLL_INTERVAL_SECS = 5 * 60;
			minPollIntervalSecs = Math.max(minPollIntervalSecs, MIN_ALLOWED_POLL_INTERVAL_SECS);

			let timeoutMillis = minPollIntervalSecs * 1000;
			this.timerId = setInterval(() => {
				this.refreshAgentClientDetails(false);
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	public onAgentClick(agentGuid: string): void {
		let newNavLoc = new NavLocation(NavType.Agent);
		newNavLoc.netViewGuid = this.network.Guid;
		newNavLoc.agentGuid = agentGuid;
		this.navService.selectLocation(newNavLoc);
	}

	private agentToDeleteGuid: string = "";

	public tryDeleteAgent(event: any, agentGuid: string): void {
		event.stopPropagation();
		this.agentToDeleteGuid = agentGuid;
		this.displayDeleteAgentConfirm = true;
	}

	public cancelDeleteAgent(): void {
		this.agentToDeleteGuid = "";
		this.displayDeleteAgentConfirm = false;
	}

	public deleteAgent(): void {
		this.netFacadeService.deleteAgent(this.agentToDeleteGuid);
		this.agentToDeleteGuid = "";
		this.displayDeleteAgentConfirm = false;
	}

	private rebuildAgentList(): void {
		this.stopTimer();

		this.agents = [];
		if (this.network) {
			this.network.AgentGuids.ForEach((agentGuid: string) => {
				let agent = this.netFacadeService.findAgent(agentGuid);
				if (agent) {
					this.agents.push(agent);
				}
			}, this);

			this.agents.sort((a, b) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1);
		}

		if (this.agents.length > 0) {
			this.refreshAgentClientDetails(false);
			this.startTimer();
		}
	}

	public onNewAgentDialogClose(): void {
		setTimeout(() => {
			this.displayNewAgentDialog = false;
		}, 0);
	}

	public refresh(): void {
		this.refreshAgentClientDetails(true);
	}

	private refreshAgentClientDetails(displayMessages: boolean): void {

		if (this.netFacadeService.isSharedNetwork(this.network.Guid)) {
			this.netFacadeService.getShares().subscribe(
				(result: GetSharesResultDto) => {
					if (this.agents) {
						this.agents.forEach((agent: AgentDto) => {
							let freshAgent = result.TargetNetViewTree.FindAgentByGuid(agent.Guid);
							if (freshAgent) {
								agent.copyAgentProperties(freshAgent);
							}
						}, this);
					}

					this.changeDetector.markForCheck();

					if (displayMessages) {
						this.messageService.displayInfoMessage("Client details have been updated");
					}
				},
				(error) => {
					if (displayMessages) {
						this.messageService.displayErrorMessage("Could not update client details");
					}
				});
		}
		else {
			this.netFacadeService.getNetViewTree("none").subscribe(
				(tree: NetViewTreeDto) => {
					if (this.agents) {
						this.agents.forEach((agent: AgentDto) => {
							let freshAgent = tree.FindAgentByGuid(agent.Guid);
							if (freshAgent) {
								agent.copyAgentProperties(freshAgent);
							}
						}, this);
					}

					this.changeDetector.markForCheck();

					if (displayMessages) {
						this.messageService.displayInfoMessage("Client details have been updated");
					}
				},
				(error) => {
					if (displayMessages) {
						this.messageService.displayErrorMessage("Could not update client details");
					}
				});
		}
	}

}
